import React, { useState, useMemo } from 'react';
import { Link, useParams, useNavigate, Navigate } from 'react-router-dom';
import PublicPageShell from '../components/PublicPageShell';
import { Input } from '../components/ui/input';
import {
  BookOpen, Lightbulb, VideoCamera, FileText, Newspaper, ChatsCircle,
  MagnifyingGlass, ArrowRight, Download, Clock, Tag, Play, ChatCircle,
  Star, Calendar,
} from '@phosphor-icons/react';

// =======================================================================
// Per-category content library (real, article-level seed data)
// =======================================================================
const CATEGORIES = {
  docs: {
    label: 'Documentation',
    icon: BookOpen,
    tint: '#002677',
    eyebrow: 'Docs',
    title: 'Build, ship & integrate with BIL.',
    subtitle: 'Full REST API reference, SDKs, webhooks and admin guides. Everything your team needs to go live.',
    kind: 'doc',
    kindLabel: 'Guide',
    items: [
      { title: 'Quickstart — your first 10 minutes on BIL',  tag: 'Getting started', time: '5 min',  blurb: 'Sign up, invite your team, import contacts, and send your first AI-scored pitch — all in under 10 minutes.' },
      { title: 'REST API reference',                          tag: 'API',             time: '45 min', blurb: 'Every endpoint, with request/response schemas and copy-paste examples in curl, Python and Node.' },
      { title: 'Authentication & JWT cookies',                tag: 'Security',        time: '8 min',  blurb: 'How BIL issues HTTP-only session cookies, refresh semantics, and best practices for session invalidation.' },
      { title: 'Webhooks, change streams & retries',          tag: 'Integrations',    time: '12 min', blurb: 'Subscribe to any resource (deals, tasks, users) and get signed deliveries with exponential backoff.' },
      { title: 'RBAC & SSO — SAML, OIDC, SCIM',               tag: 'Security',        time: '18 min', blurb: 'Model custom roles, map IdP groups, automate joiner/mover/leaver with SCIM 2.0.' },
      { title: 'Importing from HubSpot, Salesforce, Pipedrive', tag: 'Migration',     time: '15 min', blurb: 'Field-map wizard, dedupe rules, and a dry-run mode that never mutates your source.' },
      { title: 'Custom fields & data model extensions',       tag: 'Configuration',   time: '10 min', blurb: 'Add typed fields to any object, expose them to the API, and index them for search.' },
      { title: 'Audit trail & SOC 2 evidence export',         tag: 'Compliance',      time: '6 min',  blurb: 'Immutable event log, retention controls, and one-click export for your auditor.' },
    ],
  },
  tutorials: {
    label: 'Tutorials',
    icon: Lightbulb,
    tint: '#B5940A',
    eyebrow: 'Tutorials',
    title: 'Hands-on walkthroughs for admins and users.',
    subtitle: 'Step-by-step recipes curated by our customer success team — solve real-world problems in minutes.',
    kind: 'tutorial',
    kindLabel: 'Tutorial',
    items: [
      { title: 'Set up your first Data Room in 7 minutes',    tag: 'Admin',      time: '7 min',  blurb: 'Create a room, invite external reviewers, apply a watermark, and revoke access with one click.' },
      { title: 'Score your pipeline with AI Deal Readiness',  tag: 'AI',         time: '10 min', blurb: 'Pick a deal, run the scorer, interpret the 7 signals, and share the verdict with your IC.' },
      { title: 'Build a Procurement RFQ workflow',             tag: 'Procurement', time: '14 min', blurb: 'Template a multi-round RFQ, auto-notify suppliers, and score responses on price + ESG.' },
      { title: 'Automate onboarding with Workflow Builder',   tag: 'Automation', time: '11 min', blurb: 'A no-code flow that creates a checklist, assigns tasks, and posts a welcome note to Slack.' },
      { title: 'Ship a dashboard in Intelligence Studio',     tag: 'BI',         time: '9 min',  blurb: 'Drag-drop a revenue-by-sector chart and publish it to your team with row-level permissions.' },
      { title: 'Migrate legacy contracts to Documents',       tag: 'Migration',  time: '8 min',  blurb: 'Bulk upload, extract terms with AI OCR, and link contracts to their related deals or suppliers.' },
    ],
  },
  webinars: {
    label: 'Webinars',
    icon: VideoCamera,
    tint: '#E4002B',
    eyebrow: 'Webinars',
    title: 'Live sessions with product, customers & experts.',
    subtitle: 'Join upcoming webinars or catch the full on-demand archive — all sessions include slides and timestamps.',
    kind: 'webinar',
    kindLabel: 'Webinar',
    items: [
      { title: 'AI Copilots for Private Equity',              tag: 'On-demand', time: '45 min', blurb: 'Helix Partners + BIL product team on GP-grade automation, LP reporting, and portfolio copilots.' },
      { title: 'Running M&A with a 3-person team',            tag: 'On-demand', time: '38 min', blurb: 'How Nordlight Advisors scaled their deal count 4× using BIL rooms + AI Q&A triage.' },
      { title: 'Procurement Transformation in 90 days',       tag: 'Live · Mar 27', time: '60 min', blurb: 'A CPO roundtable on tail-spend reduction, supplier ESG, and contract lifecycle consolidation.' },
      { title: 'From Excel to Real-time P&L',                  tag: 'On-demand', time: '42 min', blurb: 'Meridian Retail swapped 14 tools for BIL Finance + Intelligence — here&rsquo;s their architecture.' },
      { title: 'Zero-trust data rooms for regulated deals',  tag: 'On-demand', time: '35 min', blurb: 'Watermarking, IP whitelisting, per-page access logs, and a live demo of adversarial exfiltration defence.' },
      { title: 'BIL Spring Launch Keynote 2026',               tag: 'Live · Mar 12', time: '90 min', blurb: 'The biggest product release of the year — Invest 2.0, AI Scorer v3, and a new Workflow marketplace.' },
    ],
  },
  whitepapers: {
    label: 'Whitepapers',
    icon: FileText,
    tint: '#8347AD',
    eyebrow: 'Research',
    title: 'Deep research, benchmarks & industry reports.',
    subtitle: 'Authored with practitioners, academics and our in-house AI lab. All downloads are free.',
    kind: 'whitepaper',
    kindLabel: 'PDF',
    items: [
      { title: 'The 2026 Deal Readiness Benchmark',           tag: 'M&A',        time: '32 pages', blurb: '240 transactions analysed — the 7 levers correlated with a 30%+ faster close and materially lower legal fees.' },
      { title: 'State of AI in Enterprise Finance 2026',      tag: 'AI · Finance', time: '46 pages', blurb: 'How 180 CFOs are deploying copilots across FP&A, procurement and audit — with ROI and rollout case studies.' },
      { title: 'GDPR, HIPAA & SOC 2 — a practical map',       tag: 'Compliance', time: '28 pages', blurb: 'The single overlap-and-gap matrix you actually need. Includes a cross-regulator evidence checklist.' },
      { title: 'Procurement ESG scoring: a model spec',       tag: 'Procurement', time: '22 pages', blurb: 'Open-source ESG scoring ruleset for suppliers, validated against 12 Fortune 500 procurement teams.' },
      { title: 'Operator&rsquo;s guide to workflow automation', tag: 'Operations', time: '38 pages', blurb: 'From first trigger to enterprise-grade orchestration — patterns, anti-patterns and a reference architecture.' },
    ],
  },
  blog: {
    label: 'Blog',
    icon: Newspaper,
    tint: '#1E7CA6',
    eyebrow: 'Blog',
    title: 'Product, engineering, customer stories.',
    subtitle: 'New posts every week from the BIL team — shipping notes, engineering deep-dives and field reports from customers.',
    kind: 'blog',
    kindLabel: 'Article',
    items: [
      { title: 'Shipping the AI Deal Readiness Scorer',       tag: 'Product',   time: '7 min read', blurb: 'How we went from a whiteboard sketch to a GPT-5.2 scorer trusted by 200+ deal teams, in 9 weeks.' },
      { title: 'Why we bet on MongoDB Change Streams',        tag: 'Engineering', time: '12 min read', blurb: 'Our webhook and automation layer rides on change streams — here&rsquo;s the scaling story, warts and all.' },
      { title: 'Meridian: 14 tools → one BIL',                 tag: 'Customer',  time: '6 min read', blurb: 'Field report from a 2-year consolidation inside a €1.8B retail group. What worked, what didn&rsquo;t.' },
      { title: 'Designing with Pantone Reflex Blue',         tag: 'Design',    time: '5 min read', blurb: 'Why we picked a 1970s print-first palette for an AI-era SaaS — and how it tests with enterprise buyers.' },
      { title: 'The 2-minute deploy: our internal SLA',       tag: 'Engineering', time: '9 min read', blurb: 'A rolling 30-day look at how we keep our deploy loop under 2 minutes while shipping 40+ PRs/day.' },
      { title: 'Inside BIL Invest: a marketplace for companies', tag: 'Product', time: '8 min read', blurb: 'Launch notes for our buy-sell-bid marketplace, including a walkthrough of the new valuation estimator.' },
    ],
  },
  community: {
    label: 'Community forum',
    icon: ChatsCircle,
    tint: '#00813A',
    eyebrow: 'Community',
    title: 'Ask, share, build — together.',
    subtitle: 'A global community of BIL practitioners: consultants, operators and engineers swapping templates and patterns.',
    kind: 'community',
    kindLabel: 'Thread',
    items: [
      { title: 'Show us your deal pipeline board — April edition', tag: 'Show &amp; tell', time: '142 replies', blurb: 'Our monthly share-your-setup thread. Screenshots, templates, and the odd workflow-fu trick.' },
      { title: 'How do you structure your Investor Data Room?', tag: 'Q&A',       time: '87 replies',  blurb: 'A long-running thread with contributions from 40+ heads of corp dev across EU / US / APAC.' },
      { title: 'RBAC recipes for regulated industries',         tag: 'Recipes',   time: '54 replies',  blurb: 'Pre-baked role trees for banking, healthcare, and public sector — fork & go.' },
      { title: 'Monthly meetup · Paris · May 16',               tag: 'Event',     time: '23 going',    blurb: 'Cocktails + 3 lightning talks on BIL automation. Open to users and prospects, hosted at Station F.' },
      { title: 'Feature request: dark-mode data rooms',         tag: 'Feedback',  time: '216 votes',  blurb: 'The top-voted feature request of Q1 — product is tracking, contributions welcome.' },
      { title: 'Moving from Ansarada: a migration playbook',    tag: 'Migration', time: '68 replies',  blurb: 'Community-curated list of gotchas, mappings, and scripts for a painless Ansarada → BIL migration.' },
    ],
  },
};

// =======================================================================
export default function ResourceCategoryPage() {
  const { category } = useParams();
  const navigate = useNavigate();
  const cat = CATEGORIES[category];
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    if (!cat) return [];
    if (!q) return cat.items;
    const needle = q.toLowerCase();
    return cat.items.filter((i) =>
      i.title.toLowerCase().includes(needle) ||
      (i.tag || '').toLowerCase().includes(needle) ||
      (i.blurb || '').toLowerCase().includes(needle)
    );
  }, [cat, q]);

  if (!cat) return <Navigate to="/resources" replace />;

  const Icon = cat.icon;

  return (
    <PublicPageShell
      testId={`resource-category-${category}`}
      eyebrow={cat.eyebrow}
      title={cat.title}
      subtitle={cat.subtitle}
      ctaPrimary={{ label: 'Start free', to: '/register' }}
      ctaSecondary={{ label: 'See pricing', to: '/pricing' }}
    >
      {/* Search */}
      <section className="py-10 bg-background">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${cat.label.toLowerCase()}…`}
              className="pl-12 h-12 rounded-full"
              data-testid="category-search"
            />
          </div>
        </div>
      </section>

      {/* Item grid */}
      <section className="pb-20 bg-background">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex items-center justify-between mb-6">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
              {filtered.length} {cat.kindLabel.toLowerCase()}{filtered.length === 1 ? '' : 's'}
            </p>
            <Link to="/resources" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
              ← All resources
            </Link>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-14 border border-dashed border-border rounded-sm bg-muted/20">
              <p className="text-sm text-muted-foreground">No {cat.label.toLowerCase()} match &ldquo;{q}&rdquo;.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="category-items">
              {filtered.map((item, i) => (
                <ItemCard key={i} item={item} cat={cat} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Related categories */}
      <section className="py-16 bg-[#f5f6fa] dark:bg-muted/20 border-y border-border">
        <div className="max-w-[1400px] mx-auto px-6">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#002677] mb-3 text-center">
            Also explore
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-center mb-10">
            Other resource hubs
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(CATEGORIES).filter(([k]) => k !== category).map(([key, c]) => {
              const IconC = c.icon;
              return (
                <button
                  key={key}
                  onClick={() => navigate(`/resources/${key}`)}
                  className="bg-card border border-border rounded-sm p-4 text-left hover:border-foreground/30 hover:shadow-md transition-all flex flex-col items-center text-center gap-2"
                  data-testid={`related-${key}`}
                >
                  <div
                    className="h-10 w-10 rounded-sm flex items-center justify-center"
                    style={{ background: `${c.tint}1A` }}
                  >
                    <IconC size={18} weight="duotone" color={c.tint} />
                  </div>
                  <p className="text-xs font-medium text-foreground">{c.label}</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>
    </PublicPageShell>
  );
}

function ItemCard({ item, cat }) {
  const { kind } = cat;
  const Icon = cat.icon;
  // Action label + icon per category kind
  let ActionIcon = ArrowRight;
  let actionLabel = 'Read more';
  if (kind === 'whitepaper') { ActionIcon = Download; actionLabel = 'Download PDF'; }
  else if (kind === 'webinar') { ActionIcon = Play; actionLabel = item.tag?.startsWith('Live') ? 'Register' : 'Watch replay'; }
  else if (kind === 'community') { ActionIcon = ChatCircle; actionLabel = 'Open thread'; }
  else if (kind === 'doc') { actionLabel = 'Open doc'; }
  else if (kind === 'tutorial') { ActionIcon = Play; actionLabel = 'Start tutorial'; }
  else if (kind === 'blog') { actionLabel = 'Read article'; }

  return (
    <Link
      to="/register"
      className="group bg-card border border-border rounded-sm p-6 hover:shadow-lg hover:border-foreground/30 transition-all flex flex-col"
      data-testid={`item-${item.title.toLowerCase().replace(/[^a-z0-9]+/g,'-').slice(0,50)}`}
    >
      <div className="flex items-start justify-between mb-3 gap-3">
        <div
          className="h-10 w-10 rounded-sm flex items-center justify-center flex-shrink-0"
          style={{ background: `${cat.tint}1A` }}
        >
          <Icon size={18} weight="duotone" color={cat.tint} />
        </div>
        {item.tag && (
          <span className="text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-0.5 rounded-full bg-muted/50 text-muted-foreground">
            {item.tag}
          </span>
        )}
      </div>
      <h3 className="font-heading text-lg font-semibold tracking-tight leading-snug">
        {item.title}
      </h3>
      <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1">
        {item.blurb}
      </p>
      <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
          <Clock size={11} /> {item.time}
        </span>
        <span
          className="inline-flex items-center gap-1.5 text-sm font-semibold group-hover:gap-2.5 transition-all"
          style={{ color: cat.tint }}
        >
          {actionLabel} <ActionIcon size={13} weight="bold" />
        </span>
      </div>
    </Link>
  );
}
